import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { VisualData, resolveTokens } from '@eduwrite/shared';
import { FreeDrawCanvas } from '../FreeDrawCanvas';
import { useWorkpadStore } from '../../store/useWorkpadStore';

interface Props {
    data: Extract<VisualData, { type: 'blank_canvas' }>;
    context: Record<string, number | string>;
}

export const BlankCanvasVisual: React.FC<Props> = ({ data, context }) => {
    const hintText = data.hintText ? resolveTokens(data.hintText, context) : undefined;
    const [eraserMode, setEraserMode] = useState(false);
    const { popStroke, freeDrawStrokes } = useWorkpadStore();

    const canUndo = freeDrawStrokes.length > 0;

    return (
        <View style={styles.container}>
            {hintText && (
                <Text style={styles.hint} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{hintText}</Text>
            )}
            <View style={styles.toolbar}>
                <TouchableOpacity
                    style={[styles.toolButton, !canUndo && styles.toolButtonDisabled]}
                    onPress={popStroke}
                    disabled={!canUndo}
                    accessibilityRole="button"
                    accessibilityLabel="Undo last stroke"
                    accessibilityState={{ disabled: !canUndo }}
                >
                    <Text style={styles.toolButtonText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>↩ Undo</Text>
                </TouchableOpacity>
                <TouchableOpacity
                    style={[styles.toolButton, eraserMode && styles.toolButtonActive]}
                    onPress={() => setEraserMode((prev) => !prev)}
                    accessibilityRole="button"
                    accessibilityLabel={eraserMode ? 'Switch to pen' : 'Switch to eraser'}
                >
                    <Text style={styles.toolButtonText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{eraserMode ? '✏️ Pen' : '🧼 Eraser'}</Text>
                </TouchableOpacity>
            </View>
            <View style={styles.canvasWrapper}
                accessibilityRole="image"
                accessibilityLabel="Free drawing canvas"
            >
                <FreeDrawCanvas eraserMode={eraserMode} />
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    hint: {
        fontSize: 16,
        fontWeight: '500',
        color: '#64748B',
        marginBottom: 8,
        textAlign: 'center',
    },
    toolbar: {
        flexDirection: 'row',
        gap: 12,
        marginBottom: 8,
        justifyContent: 'center',
    },
    toolButton: {
        height: 48,
        paddingHorizontal: 16,
        borderRadius: 12,
        backgroundColor: '#F1F5F9',
        alignItems: 'center',
        justifyContent: 'center',
    },
    toolButtonActive: {
        backgroundColor: '#FEF9C3',
        borderColor: '#FDE047',
        borderWidth: 2,
    },
    toolButtonDisabled: {
        opacity: 0.5,
    },
    toolButtonText: {
        fontSize: 14,
        fontWeight: '700',
        color: '#1E293B',
    },
    canvasWrapper: {
        flex: 1,
        minHeight: 120,
    },
});
